<template>
  <div class="heel-frequency-step">
    <div class="heel-selection-content">
      <!-- Title -->
      <div class="title-section">
        <h2 class="step-title">{{ step.title }}</h2>
        <p v-if="false" class="step-subtitle">{{ step.subtitle }}</p>
      </div>

      <!-- Frequency Options -->
      <div class="frequency-options">
        <button
          v-for="option in step.frequencyOptions"
          :key="option.value"
          class="frequency-option"
          :class="{ selected: selectedFrequency === option.value }"
          @click="selectFrequency(option.value)"
        >
          {{ option.label }}
        </button>
      </div>

      <!-- Heel Size Options (shown only if sometimes or often selected) -->
      <div v-if="showHeelSizes" class="heel-sizes-section">
        <h3 class="sizes-title">Какой размер каблука вы предпочитаете?</h3>
        <p class="sizes-subtitle">Выберите все подходящие варианты</p>

        <div class="sizes-options">
          <label
            v-for="size in step.heelSizes"
            :key="size.value"
            class="size-option"
          >
            <input
              type="checkbox"
              :value="size.value"
              v-model="selectedHeelSizes"
              class="size-checkbox"
            />
            <img :src="getHeelSizeImage(size.value)" :alt="size.label" class="size-img" />
            <span class="size-label">{{ size.label }}</span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useTasteClarificationModal } from '@/features/taste/store'
import type { TasteStep, HeelFrequencyValue, HeelSizeValue } from '@/features/taste/types/taste-config'
import { getHeelSizeImage } from '@/features/taste/utils/heel-size-images'

interface Props {
  step: TasteStep & { type: 'heel_frequency' }
}

const props = defineProps<Props>()
const tasteStore = useTasteClarificationModal()

const selectedFrequency = ref<HeelFrequencyValue | null>(null)
const selectedHeelSizes = ref<HeelSizeValue[]>([])

const showHeelSizes = computed(() => {
  return selectedFrequency.value === 'sometimes' || selectedFrequency.value === 'often'
})

function selectFrequency(frequency: HeelFrequencyValue) {
  selectedFrequency.value = frequency
  tasteStore.updateTasteData(props.step.id, frequency)

  if (frequency === 'never') {
    selectedHeelSizes.value = []
    tasteStore.updateTasteData(`${props.step.id}_heelSizes`, [])
  }
}

watch(selectedHeelSizes, (newSizes) => {
  tasteStore.updateTasteData(`${props.step.id}_heelSizes`, newSizes)
})

onMounted(() => {
  selectedFrequency.value = tasteStore.tasteModal.tasteData[props.step.id] || null
  selectedHeelSizes.value = [...(tasteStore.tasteModal.tasteData[`${props.step.id}_heelSizes`] || [])] as HeelSizeValue[]
})
</script>

<style scoped>
.heel-frequency-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.heel-selection-content {
  max-width: 500px;
  width: 100%;
}

.title-section {
  margin-bottom: 32px;
  text-align: left;
}

.step-title {
  font-size: 24px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0;
}

.step-subtitle {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0;
}

.frequency-options {
  display: flex;
  gap: 8px;
  margin-bottom: 32px;
}

.frequency-option {
  padding: 20px 24px;
  border: 2px solid #e5e5e7;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 16px;
  font-weight: 500;
  color: var(--active-100);
}

.frequency-option:hover {
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.15);
}

.frequency-option.selected {
  border-color: var(--active-100);
  background: var(--active-100);
  color: var(--white-100);
}

.heel-sizes-section {
  text-align: left;
}

.sizes-title {
  font-size: 20px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 8px 0;
}

.sizes-subtitle {
  font-size: 14px;
  color: #666;
  margin: 0 0 20px 0;
}

.sizes-options {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.size-option {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 12px 16px;
  border: 2px solid #e5e5e7;
  border-radius: 8px;
  background: white;
  transition: all 0.3s ease;
}

.size-option:hover {
  border-color: var(--primary-blue-100);
  background: rgba(0, 122, 255, 0.05);
}

.size-checkbox {
  width: 20px;
  height: 20px;
  accent-color: var(--primary-blue-100);
}

.size-img {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.size-label {
  font-size: 16px;
  color: #666;
  font-weight: 500;
}

.size-option:hover .size-label {
  color: var(--active-100);
}

@media (min-width: 393px) and (max-width: 767px) {
  .heel-frequency-step {
    padding: 30px 0 0 0;
    align-self: stretch;
  }

  .heel-selection-content {
    max-width: none;
    width: 100%;
    padding: 0 20px 20px;
    gap: 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }

  .title-section {
    margin-bottom: 0;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 0;
    align-items: flex-start;
    align-self: stretch;
  }

  .step-title {
    font-size: 32px;
    line-height: 1.09;
    font-weight: 700;
    color: #232F45;
    text-align: left;
    margin-bottom: 20px;
  }

  .step-subtitle {
    font-size: 15px;
    line-height: 1.3;
  }

  .frequency-options {
    display: flex;
    gap: 12px;
    margin-bottom: 0;
    align-self: stretch;
    align-items: stretch;
  }

  .frequency-option {
    padding: 12px;
    border: 1px solid #E6E3EC;
    border-radius: 12px;
    flex: 1;
  }

  .heel-sizes-section {
    text-align: left;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    overflow-y: auto;
    border-radius: 20px;
    background: rgba(110, 133, 203, 0.08);
  }

  .sizes-title {
    font-size: 24px;
    line-height: 1.09;
    font-weight: 600;
    color: #232F45;
    margin: 0;
  }

  .sizes-subtitle {
    font-size: 12px;
    line-height: 1.2;
    color: rgba(35, 47, 69, 0.8);
    margin: 0;
  }

  .sizes-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .size-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 20px;
    border: none;
    border-radius: 8px;
    background: white;
  }

  .size-img {
    width: 60px;
    height: 60px;
  }

  .size-label {
    font-size: 15px;
    line-height: 1.3;
    font-weight: 700;
    color: #232F45;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .heel-frequency-step {
    padding: 30px 0 0 0;
    align-self: stretch;
    flex:1;
  }

  .heel-selection-content {
    max-width: none;
    width: 100%;
    padding: 0 20px 20px;
    gap: 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    flex:1;
  }

  .title-section {
    margin-bottom: 0;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 0;
    align-items: flex-start;
    align-self: stretch;
  }

  .step-title {
    font-size: 32px;
    line-height: 1.09;
    font-weight: 700;
    color: #232F45;
    text-align: left;
    margin: 0;
  }

  .step-subtitle {
    font-size: 15px;
    line-height: 1.3;
  }

  .frequency-options {
    display: flex;
    gap: 12px;
    margin-bottom: 0;
    align-self: stretch;
    align-items: stretch;
  }

  .frequency-option {
    padding: 12px;
    border: 1px solid #E6E3EC;
    border-radius: 12px;
    flex: 1;
  }

  .heel-sizes-section {
    text-align: left;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    border-radius: 20px;
    background: rgba(110, 133, 203, 0.08);
    flex:1;
  }

  .sizes-title {
    font-size: 24px;
    line-height: 1.09;
    font-weight: 600;
    color: #232F45;
    margin: 0;
  }

  .sizes-subtitle {
    font-size: 12px;
    line-height: 1.2;
    color: rgba(35, 47, 69, 0.8);
    margin: 0;
  }

  .sizes-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .size-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    border: none;
    border-radius: 8px;
    background: white;
  }

  .size-img {
    width: 60px;
    height: 60px;
  }

  .size-label {
    font-size: 15px;
    line-height: 1.3;
    font-weight: 700;
    color: #232F45;
  }
}

@media (min-width: 1024px) and (max-width: 1439px) {
  .heel-frequency-step {
    padding: 0;
  }

  .heel-selection-content {
    max-width: none;
    width: 100%;
    padding: 40px;
    gap: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .title-section {
    margin-bottom: 20px;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    align-self: stretch;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
    color: #232F45;
    text-align: center;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
  }

  .frequency-options {
    display: flex;
    gap: 12px;
    margin-bottom: 0;
    align-self: stretch;
    align-items: stretch;
  }

  .frequency-option {
    padding: 12px;
    border: 1px solid #E6E3EC;
    border-radius: 12px;
    flex: 1;
  }

  .heel-sizes-section {
    text-align: left;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    border-radius: 20px;
    background: rgba(110, 133, 203, 0.08);
  }

  .sizes-title {
    font-size: 36px;
    line-height: 1.2;
    font-weight: 500;
    color: #232F45;
    margin: 0;
  }

  .sizes-subtitle {
    font-size: 14px;
    line-height: 1.5;
    color: rgba(35, 47, 69, 0.8);
    margin: 0;
  }

  .sizes-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .size-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    border: none;
    border-radius: 8px;
    background: white;
  }

  .size-img {
    width: 60px;
    height: 60px;
  }

  .size-label {
    font-size: 20px;
    line-height: 1.3;
    font-weight: 700;
    color: #232F45;
  }
}

@media (min-width: 1440px) and (max-width: 1919px) {
  .heel-frequency-step {
    padding: 0;
  }

  .heel-selection-content {
    max-width: none;
    width: 100%;
    padding: 40px;
    gap: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .title-section {
    margin-bottom: 20px;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    align-self: stretch;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
    color: #232F45;
    text-align: center;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
  }

  .frequency-options {
    display: flex;
    gap: 12px;
    margin-bottom: 0;
    align-self: stretch;
    align-items: stretch;
  }

  .frequency-option {
    padding: 12px;
    border: 1px solid #E6E3EC;
    border-radius: 12px;
    flex: 1;
  }

  .heel-sizes-section {
    text-align: left;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    border-radius: 20px;
    background: rgba(110, 133, 203, 0.08);
  }

  .sizes-title {
    font-size: 36px;
    line-height: 1.2;
    font-weight: 500;
    color: #232F45;
    margin: 0;
  }

  .sizes-subtitle {
    font-size: 14px;
    line-height: 1.5;
    color: rgba(35, 47, 69, 0.8);
    margin: 0;
  }

  .sizes-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .size-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    border: none;
    border-radius: 8px;
    background: white;
  }

  .size-img {
    width: 60px;
    height: 60px;
  }

  .size-label {
    font-size: 20px;
    line-height: 1.3;
    font-weight: 700;
    color: #232F45;
  }
}

@media (min-width: 1920px) {
  .heel-frequency-step {
    padding: 0;
  }

  .heel-selection-content {
    max-width: none;
    width: 100%;
    padding: 40px 120px;
    gap: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .title-section {
    margin-bottom: 20px;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    align-self: stretch;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
    color: #232F45;
    text-align: center;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
  }

  .frequency-options {
    display: flex;
    gap: 12px;
    margin-bottom: 0;
    align-self: stretch;
    align-items: stretch;
  }

  .frequency-option {
    padding: 12px;
    border: 1px solid #E6E3EC;
    border-radius: 12px;
    flex: 1;
  }

  .heel-sizes-section {
    text-align: left;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    border-radius: 20px;
    background: rgba(110, 133, 203, 0.08);
  }

  .sizes-title {
    font-size: 36px;
    line-height: 1.2;
    font-weight: 500;
    color: #232F45;
    margin: 0;
  }

  .sizes-subtitle {
    font-size: 14px;
    line-height: 1.5;
    color: rgba(35, 47, 69, 0.8);
    margin: 0;
  }

  .sizes-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .size-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    border: none;
    border-radius: 8px;
    background: white;
  }

  .size-img {
    width: 60px;
    height: 60px;
  }

  .size-label {
    font-size: 20px;
    line-height: 1.3;
    font-weight: 700;
    color: #232F45;
  }
}
</style>

